/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import styled from "styled-components"
import tw from "twin.macro"

import { Icon } from "@components/icons"
import scrollReveal from "@utils/scroll.reveal"
import { srConfig } from "@config"
import { RocketIcon } from "./icons"

const StyledOtherProjects = styled.div`
  ${tw`min-h-full pb-12 text-suvaGrey mb-12`}

  .headline {
    ${tw`text-neutralLight font-semibold`}
  }

  .wrapper {
    ${tw`grid md:grid-cols-3 gap-10`}
  }

  .button {
    ${tw`flex items-center justify-center`}
    .more-button {
      ${tw`bg-transparent border border-solid border-deepSky text-deepSky mt-8 px-8 py-4 rounded-lg transition-all cursor-pointer`}
      &:hover,
      &:focus {
        ${tw`bg-lightDeepSky`}
      }
    }
  }
`

const StyledCard = styled.div`
  ${tw`border rounded-lg border-solid border-suvaGrey p-4 cursor-default select-none transition-all`}

  &:hover,
  &:focus {
    ${tw`shadow-xl transform -translate-y-1`}
  }

  .upper {
    ${tw`flex items-center`}

    .project-logo {
      ${tw`w-10`}
    }

    .project-links {
      ${tw`flex gap-5 justify-end flex-1`}
      a {
        ${tw`text-suvaGrey transition-all`}
        &:hover,
        &:focus {
          ${tw`text-deepSky`}
        }
      }
    }
  }

  .content {
    ${tw``}

    &__title {
      ${tw`mb-0 flex gap-5 items-end`}
    }

    &__company {
      ${tw`font-mono text-sm text-green-500`}
    }

    .project-tech-list {
      ${tw`text-xs list-none flex items-center flex-wrap p-0 text-gray-500 font-mono`}

      li {
        ${tw`mr-4 mb-1`}
      }
    }
  }
`

const OtherProjects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/otherProjects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
              logo
              company
            }
            html
          }
        }
      }
    }
  `)

  const [showMore, setShowMore] = useState(false)
  const revealProjects = useRef([])
  const revealTitle = useRef(null)
  const GRID_LIMIT = 6
  const projects = data.projects.edges.filter(({ node }) => node)
  const firstSix = projects.slice(0, GRID_LIMIT)
  const projectsToShow = showMore ? projects : firstSix

  useEffect(() => {
    scrollReveal.reveal(revealTitle.current, srConfig())
    revealProjects.current.forEach((ref, i) =>
      scrollReveal.reveal(ref, srConfig(i * 100))
    )
  }, [])

  return (
    <StyledOtherProjects>
      <h2 className="headline" ref={revealTitle}>
        Other projects
      </h2>

      <TransitionGroup className="projects-grid">
        <div className="wrapper">
          {projectsToShow &&
            projectsToShow.map(({ node }, i) => {
              const { frontmatter, html } = node
              const {
                github,
                external,
                title,
                tech,
                company,
                logo,
              } = frontmatter

              return (
                <CSSTransition
                  key={title}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}
                >
                  <StyledCard
                    tabIndex="0"
                    ref={el => {
                      revealProjects.current[i] = el
                    }}
                    style={{
                      transitionDelay: `${
                        i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0
                      }ms`,
                    }}
                  >
                    <div className="upper">
                      <img className="project-logo" src={logo} alt="logo" />
                      <div className="project-links">
                        {github && (
                          <a
                            href={github}
                            aria-label="GitHub Link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon name="GitHub" />
                          </a>
                        )}
                        {external && (
                          <a
                            href={external}
                            aria-label="Website Link"
                            className="external"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon name="External" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="content">
                      <h4 className="content__title">
                        <span>{title}</span>
                        {company && (
                          <span className="content__company">{company}</span>
                        )}
                      </h4>
                      <div
                        tw="text-sm"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                      {tech.length && (
                        <ul className="project-tech-list">
                          {tech.map(t => (
                            <li key={t}>{t}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </StyledCard>
                </CSSTransition>
              )
            })}
        </div>
      </TransitionGroup>

      {projectsToShow.length > 6 && (
        <div className="button">
          <button
            type="button"
            className="more-button"
            onClick={() => setShowMore(!showMore)}
          >
            Show {showMore ? "Less" : "More"}
          </button>
        </div>
      )}
    </StyledOtherProjects>
  )
}

export default OtherProjects
